import {useNuxtApp, usePage} from "#imports";
import {storeToRefs} from "pinia";
import type {HblProduct} from '@/utils/types'

export const useEpoq = function () {
    async function search(term: string){
        const config = useRuntimeConfig().public
        const limit = 50

        const { result } = await $fetch('/api/epoq/search',{
            method: 'post',
            body: {
                locakey: useNuxtApp().$i18n.locale.value,
                limit: limit,
                tenantId: config.epoqTenantId,
                sessionId: generateSessionId(),
                query: term,
                style: 'onlyId',
                full: true
            },
        })

        // content result with redirect
        if(result?.['content-matches']?.['content-match']?.['directlink']?.['$']) {
            await navigateTo(result?.['content-matches']?.['content-match']?.['url']?.['$'], {
                redirectCode: 301,
                external: true
            });
            return abortNavigation()
        }

        // no results
        if(!result?.findings?.finding) {
            return []
        }


        let productNumbers = []
        if(Array.isArray(result?.findings?.finding)) {
            productNumbers = result?.findings?.finding?.map(product => product?.['match-item']?.['@node_ref'])
        } else {
            productNumbers = [result?.findings?.finding?.['match-item']?.['@node_ref']]
        }
        const { getProductsFromProductNumbers } = usePage()

        const products = await getProductsFromProductNumbers(productNumbers, limit)


        const searchProducts: HblProduct[] = []
        productNumbers.forEach((number: string) => {
            const product = products.find(product => product.sku === number)
            if(product) {
                product.url = product.url + `?epoqId=${result.qid['$']}`
                searchProducts.push(product)
            }
        })

        return searchProducts
    }

    async function typeAhead(term: string){
        const config = useRuntimeConfig().public



        const { result } = await $fetch('/api/epoq/search',{
            method: 'post',
            body: {
                locakey: useNuxtApp().$i18n.locale.value,
                limit: 5,
                tenantId: config.epoqTenantId,
                sessionId: generateSessionId(),
                query: term,
            }
        })

        // no results
        if(!result?.findings?.finding) {
            return null
        }

        // wrap in array if only one element
        if(result?.findings?.finding?.suggestion) {
            result.findings.finding = [result?.findings?.finding]
        }

        let products: Record<string, string|null>[] = []
        let additionalProducts: Record<string, string|null>[] = []
        let categories: Record<string, string|null>[] = []
        let suggestions: Record<string, string|null>[] = []

        const findings = Array.isArray(result?.findings?.finding) ? result?.findings?.finding : []

        for (const [index, find] of findings.entries()) {
            let suggestionVisibility = false
            if(find?.suggestion?.doc) {
                // wrap in array if only one element
                if(find?.suggestion?.doc?.attribute) {
                    find.suggestion.doc = [find?.suggestion?.doc]
                }
                find?.suggestion?.doc?.forEach((doc: Record<string,Record<any, any>[]>) => {
                    let productVisibility = false
                    const product = {}
                    for (const attribute of doc.attribute) {
                        if (attribute['@name'] === 'title') {
                            product.name = attribute['$']
                        }
                        if (attribute['@name'] === 'link') {
                            product.url = attribute['$'] + `?epoqId=${result['qid']['$']}`
                        }
                        if (attribute['@name'] === 'g:image_link') {
                            product.image = attribute['$']?.startsWith('http') ? attribute['$'] : config.imgProxyUrl + attribute['$']
                        }
                        // if not active in saleschannel
                        if(attribute['@name'] === `c:saleschannel_${config.salesChannelId}` && attribute['$'] == '1') {
                            productVisibility = true
                            suggestionVisibility = true
                        }
                    }

                    if(productVisibility) {
                        if(index === 0) {
                            products.push(product)
                        } else if(find['suggestion']['@field'] === 'e:variant_ids') {
                            additionalProducts.push(product)
                        }
                    }
                })
            }

            // if no product of that suggestion is visible don't display category/suggestion
            if(!suggestionVisibility || find['suggestion']['@field'] === 'e:variant_ids') {
                continue;
            }

            const searchResult = {
                name: find['suggestion']['$'],
                url: `/search?search=${find['suggestion']['$']}&epoqId=${result['qid']['$']}`,
                image: null,
            }

            if (find['suggestion']['@field'] === 'g:product_type') {
                categories.push(searchResult)
            } else {
                suggestions.push(searchResult)
            }
        }

        // Use same logic as in SW5
        // 1. Get from all products as many products as = max results - 1, but there will be always only one product!
        // 2. Get from all categories as many as = max results - number of products
        // 3. Get from all other suggestions as many as = max results - (number of products + number of categories)
        // 4. Get all additional products (products if someone search for product number)
        const maxResults = 6


        // 1. Get from all products as many products as = max results - 1
        products = products?.slice(0, maxResults - 1) ?? []

        // 2. Get from all categories as many as = max results - number of products
        categories = categories.slice(0, maxResults - products.length) ?? []

        // 3. Get from all other suggestions as many as = max results - (number of products + number of categories)
        // Filter out from suggestions every result that is already in result set
        const currentResults = [...products, ...categories]

        const names = currentResults.map(result => result.name)
        suggestions = suggestions.filter(suggestion => !names.includes(suggestion.name))
        suggestions = suggestions.slice(0, maxResults - currentResults.length) ?? []

        // add additional products if there is space for them
        additionalProducts = additionalProducts.slice(0, maxResults - currentResults.length - suggestions.length) ?? []

        return [...products, ...additionalProducts, ...categories, ...suggestions.slice(0, maxResults - currentResults.length) ?? []]
    }


    async function reportClickThrough(epoqId: string, productNumber: string) {
        const config = useRuntimeConfig().public
        const { customer } = storeToRefs(useCustomer())

        await $fetch('/api/epoq/report-click',{
            method: 'post',
            body: {
                tenantId: config.epoqTenantId,
                sessionId: generateSessionId(),
                qId: epoqId,
                productNumber: productNumber,
                ...(customer?.value?.customerNumber && {customerNumber: customer?.value?.customerNumber})
            }
        })
    }

    async function getRecommendations(rule: string, productNumber: string) {
        const config = useRuntimeConfig().public
        const response = await $fetch('/api/epoq/recommendations',{
            method: 'post',
            body: {
                tenantId: config.epoqTenantId,
                sessionId: generateSessionId(),
                productNumber: productNumber,
                rule: rule
            }
        })


        if(!response?.recommendations?.domain?.items?.item) {
            return null
        }

        const productNumbers = response?.recommendations?.domain?.items?.item?.map(item => item['@id'])

        const { getProductsFromProductNumbers } = usePage()

        const products = await getProductsFromProductNumbers(productNumbers, productNumbers.length)

        products.map(product => {
            const epoqProduct = response?.recommendations?.domain?.items?.item?.find(item => item['@id'] === product.sku)
            product.feedbackUrl = epoqProduct['@feedbackurl']
            return product
        })

        return products
    }

    /**
     * Returns session
     */
    function generateSessionId() {
        const sessionId = useCookie('epoq_session_id')
        if(!sessionId.value) {
            sessionId.value = crypto.randomUUID()
        }

        return sessionId.value
    }

    return {
        search,
        typeAhead,
        reportClickThrough,
        getRecommendations
    }
}
